import { motion } from 'framer-motion';
import { Play, BookOpen } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

// Fallback background for when video hasn't loaded
const FALLBACK_BG_IMAGE = 'linear-gradient(to bottom, #0a0a0a 0%, #1a1512 50%, #0a0a0a 100%)';

const HeroSection = ({ heroData }) => {
  const [showTrailer, setShowTrailer] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [titleImageLoaded, setTitleImageLoaded] = useState(false);
  const [titleImageError, setTitleImageError] = useState(false);
const trailerIframeRef = useRef(null);

  // Preload title image
  useEffect(() => {
    if (heroData?.title_image_url && heroData.title_image_url.trim() !== '') {
      const img = new Image();
      img.onload = () => setTitleImageLoaded(true);
      img.onerror = () => setTitleImageError(true);
      img.src = heroData.title_image_url;
    }
  }, [heroData?.title_image_url]);

  const scrollToChapters = () => {
    const chaptersSection = document.getElementById('chapters');
    if (chaptersSection) {
      chaptersSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getYouTubeEmbedUrl = (url) => {
  if (!url) return '';
  const videoId = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
  return videoId
    ? `https://www.youtube.com/embed/${videoId[1]}?autoplay=1&mute=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&controls=1`
    : url;
};
const handleTrailerIframeLoad = () => {
  if (trailerIframeRef.current?.contentWindow) {
    trailerIframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'playVideo',
        args: []
      }),
      '*'
    );

    trailerIframeRef.current.contentWindow.postMessage(
      JSON.stringify({
        event: 'command',
        func: 'mute',
        args: []
      }),
      '*'
    );
  }
};
  return (
    <section className="relative h-screen w-full overflow-hidden bg-void" data-testid="hero-section">
      {/* Background: Video with fallback gradient */}
      {heroData?.hero_video_url ? (
        <>
          {/* Show gradient while video loads */}
          {!videoLoaded && (
            <div
              className="absolute inset-0"
              style={{ background: FALLBACK_BG_IMAGE }}
            />
          )}
          <video
            className="video-background"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setVideoLoaded(true)}
            onCanPlay={() => setVideoLoaded(true)}
            style={{ opacity: videoLoaded ? 1 : 0, transition: 'opacity 0.5s ease' }}
          >
            <source src={heroData.hero_video_url} type="video/mp4" />
          </video>
        </>
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: FALLBACK_BG_IMAGE }}
        />
      )}

      <div className="absolute inset-0 hero-gradient" />

      <div className="relative z-20 h-full flex flex-col items-center justify-center px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4"
          data-testid="hero-title"
        >
          {heroData?.title_image_url && heroData.title_image_url.trim() !== '' && !titleImageError ? (
            <h1 className="m-0">
              <span className="sr-only">{heroData?.title || 'The Oath Of The Unhuan'}</span>
              <img
                src={heroData.title_image_url}
                alt=""
                aria-hidden="true"
                className="max-w-full h-auto max-h-[60vh] object-contain"
                style={{ 
                  filter: 'drop-shadow(0 0 30px rgba(192, 160, 98, 0.3))',
                  opacity: titleImageLoaded ? 1 : 0,
                  transition: 'opacity 0.5s ease'
                }}
                onLoad={() => setTitleImageLoaded(true)}
                onError={() => setTitleImageError(true)}
              />
            </h1>
          ) : (
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-cinzel text-center text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-400 tracking-tighter uppercase text-shadow-gold">
              {heroData?.title || 'The Oath Of The Unhuan'}
            </h1>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-6 mt-12"
        >
          <div className="flex flex-col sm:flex-row gap-6">
            <button
              data-testid="trailer-button"
              onClick={() => {
                if (heroData?.trailer_url) {
                  setShowTrailer(true);
                }
              }}
              disabled={!heroData?.trailer_url}
              className="relative px-8 py-4 bg-transparent border border-gold text-gold font-manrope font-semibold tracking-widest uppercase hover:bg-gold hover:text-black transition-all duration-300 overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="inline-block w-5 h-5 mr-2" />
              Trailer
            </button>

            <button
              data-testid="enter-chronicles-button"
              onClick={scrollToChapters}
              className="px-8 py-4 text-gray-400 hover:text-white transition-colors duration-300 tracking-widest uppercase font-manrope text-sm border-b border-transparent hover:border-white/20"
            >
              <BookOpen className="inline-block w-5 h-5 mr-2" />
              Enter the Chronicles
            </button>
          </div>

          {/* Small text under buttons */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="text-center text-sm tracking-wide"
            style={{
              fontFamily: 'Cinzel, serif',
              color: '#C6A85A',
              textShadow: '0 0 8px rgba(224, 200, 120, 0.4)'
            }}
          >
            Explore the names that shape destiny.
          </motion.p>

          {/* Scroll arrow */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ 
              opacity: 1, 
              y: [0, 10, 0]
            }}
            transition={{ 
              opacity: { duration: 1, delay: 0.8 },
              y: { 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }
            }}
            onClick={() => {
              const codexSection = document.getElementById('living-codex');
              if (codexSection) {
                codexSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="mt-8 p-3 rounded-full border border-gold/30 hover:border-gold transition-all duration-300"
            style={{
              color: '#C6A85A'
            }}
            data-testid="scroll-arrow"
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </motion.button>
        </motion.div>
      </div>

      {showTrailer && heroData?.trailer_url && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setShowTrailer(false)}
          data-testid="trailer-modal"
        >
          <div className="relative w-full max-w-5xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-12 right-0 text-white hover:text-gold transition-colors"
              data-testid="close-trailer-button"
            >
              Close
            </button>
            <div className="youtube-container">
              <iframe
  ref={trailerIframeRef}
  src={getYouTubeEmbedUrl(heroData.trailer_url)}
  title="Trailer"
  onLoad={handleTrailerIframeLoad}
  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
  allowFullScreen
/>
              
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
